// CoverageMatrixV2.tsx — Coverage-Matrix Wave 2 (UI-A, AI-kh9r).
// The interactive Coverage tab: completeness-recolored matrix over ALL 36 states,
// State→District→Pincode drill, and the right-side TerritoryPanel. Binds Squad
// CE's coverage-matrix-v2 + coverage-drill endpoints; the panel binds
// territory-detail. Spec §2 (recolor) / §3 (drill + all-states) / §4 (panel).

import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, RefreshCcw, AlertCircle } from 'lucide-react';
import {
  leadRouterV2Api,
  type RouterStaff, type RouterStaffGraph,
  type CoverageMatrixV2Response, type CoverageDrillV2,
  type CoverageColorV2, type MatchTypeV2,
} from '@/api/leadRouter';
import {
  COVERAGE_FILL, COVERAGE_INK, COVERAGE_GLYPH,
  matrixCounters, overlapBadge, buildBreadcrumb, cellTerritory,
  type DrillPath, type DrillLevel,
} from './coverageLogic';
import { TerritoryPanel } from './TerritoryPanel';

export interface CoverageMatrixV2Props {
  staff: RouterStaff[];
  staffGraph: RouterStaffGraph;
  /** key → label for product groups (for panel headers). */
  groupLabels?: Record<string, string>;
  fallbackUserId: string | null;
  onEditEscalation: (managerId: string) => void;
  onEditDealers: (managerId: string | null) => void;
}

interface PanelTarget {
  productGroupKey: string;
  productLabel: string;
  matchType: MatchTypeV2;
  matchValue: string;
  breadcrumbLabel: string;
}

export function CoverageMatrixV2(props: CoverageMatrixV2Props) {
  const { staff, staffGraph, groupLabels, fallbackUserId, onEditEscalation, onEditDealers } = props;

  const [matrix, setMatrix] = useState<CoverageMatrixV2Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill state: null = top state matrix; else drilled into one product+state (+district).
  const [path, setPath] = useState<DrillPath | null>(null);
  const [drill, setDrill] = useState<CoverageDrillV2 | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const [panel, setPanel] = useState<PanelTarget | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const labelOf = (key: string) => groupLabels?.[key] ?? key;

  const loadMatrix = async () => {
    setLoading(true); setError(null);
    try {
      setMatrix(await leadRouterV2Api.getCoverageMatrixV2('EXEC'));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Coverage matrix unavailable');
    } finally { setLoading(false); }
  };
  useEffect(() => { void loadMatrix(); }, [refreshKey]);

  // Load a drill level whenever the path changes to a drilled state.
  useEffect(() => {
    if (!path?.state) { setDrill(null); return; }
    let cancelled = false;
    setDrillLoading(true);
    leadRouterV2Api
      .getCoverageDrill({ productGroupKey: path.productGroupKey, state: path.state, district: path.district ?? null })
      .then((d) => { if (!cancelled) setDrill(d); })
      .catch(() => { if (!cancelled) setDrill(null); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [path?.productGroupKey, path?.state, path?.district, refreshKey]);

  const cellByKey = useMemo(() => {
    const m = new Map<string, CoverageMatrixV2Response['cells'][number]>();
    if (matrix) for (const c of matrix.cells) m.set(`${c.productGroupKey}|${c.state}`, c);
    return m;
  }, [matrix]);

  const counts = matrix ? matrixCounters(matrix.cells) : { full: 0, partial: 0, gaps: 0, total: 0 };
  const refresh = () => setRefreshKey((k) => k + 1);

  // Open the panel for a cell + optionally drill deeper.
  function openTerritory(productGroupKey: string, level: DrillLevel, unit: string, trail: string) {
    const terr = cellTerritory(level, unit);
    setPanel({ productGroupKey, productLabel: labelOf(productGroupKey), matchType: terr.matchType, matchValue: terr.matchValue, breadcrumbLabel: trail });
  }

  function drillState(productGroupKey: string, state: string) {
    setPath({ productGroupKey, productLabel: labelOf(productGroupKey), state });
    openTerritory(productGroupKey, 'state', state, `${labelOf(productGroupKey)} · ${state}`);
  }
  function drillDistrict(district: string) {
    if (!path?.state) return;
    setPath({ ...path, district });
    openTerritory(path.productGroupKey, 'district', district, `${path.productLabel ?? path.productGroupKey} · ${path.state} · ${district}`);
  }
  function selectPincode(pincode: string) {
    if (!path?.state) return;
    openTerritory(path.productGroupKey, 'pincode', pincode, `${path.productLabel ?? path.productGroupKey} · ${path.state} · ${path.district} · ${pincode}`);
  }

  const crumbs = buildBreadcrumb(path);
  function goCrumb(level: Crumb['level']) {
    if (level === 'root') { setPath(null); setPanel(null); }
    else if (level === 'state' && path) { setPath({ productGroupKey: path.productGroupKey, productLabel: path.productLabel, state: path.state }); setPanel(null); }
  }

  if (loading) return <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 8 }}>Loading coverage…</div>;
  if (error || !matrix) {
    return (
      <div style={{ padding: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>
          <AlertCircle size={15} /> {error ?? 'Coverage matrix unavailable — seed product groups & assignments first.'}
        </div>
        <button type="button" onClick={refresh} style={btn}><RefreshCcw size={13} /> Retry</button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <SectionHead
        title="Coverage Matrix"
        sub="Every product category × all 36 India states/UTs. Cells are colored by completeness of explicit assignment — click any cell to drill State ▸ District ▸ Pincode and open the territory panel."
      />

      {/* Header counters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 14 }}>
        <Stat v={counts.full} k="Fully assigned" color="green" />
        <Stat v={counts.partial} k="Partially assigned" color="yellow" />
        <Stat v={counts.gaps} k="No explicit owner" color="red" />
        <Stat v={`${matrix.groups.length} · ${matrix.states.length}`} k="Products · States" color="neutral" />
      </div>

      {/* Breadcrumb (shown once drilled) */}
      {path && (
        <div data-testid="cov-breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 12 }}>
          {crumbs.map((c, i) => (
            <span key={`${c.level}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <ChevronRight size={12} style={{ opacity: 0.5 }} />}
              <button type="button" onClick={() => goCrumb(c.level)}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontWeight: i === crumbs.length - 1 ? 700 : 500, color: i === crumbs.length - 1 ? 'var(--ink-0)' : 'var(--forest,#2f7d3b)', textDecoration: i === crumbs.length - 1 ? 'none' : 'underline' }}>
                {c.label}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* ── Top state matrix ── */}
      {!path && (
        <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)' }}>
          <table data-testid="cov-matrix" style={{ borderCollapse: 'collapse', fontSize: 11.5, width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...th, position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left', zIndex: 1 }}>Product ╲ State</th>
                {matrix.states.map((s) => <th key={s} style={th} title={s}>{abbr(s)}</th>)}
              </tr>
            </thead>
            <tbody>
              {matrix.groups.map((g) => (
                <tr key={g}>
                  <td style={{ ...td, fontWeight: 700, position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left', whiteSpace: 'nowrap' }}>{labelOf(g)}</td>
                  {matrix.states.map((s) => {
                    const c = cellByKey.get(`${g}|${s}`);
                    const color: CoverageColorV2 = c?.cell ?? 'red';
                    const badge = overlapBadge(c?.overlap ?? { exec: false, manager: false });
                    return (
                      <td key={s} data-testid={`cell-${g}-${s}`} title={cellTitle(g, s, c)}
                        onClick={() => drillState(g, s)}
                        style={{ ...td, background: COVERAGE_FILL[color], color: COVERAGE_INK[color], cursor: 'pointer', textAlign: 'center', position: 'relative' }}>
                        {COVERAGE_GLYPH[color]}
                        {badge.show && <OverlapDot kind={badge.kind!} />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Drill grid (districts or pincodes) ── */}
      {path && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)', padding: 12 }}>
          {drillLoading && <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>Loading {path.district ? 'pincodes' : 'districts'}…</div>}
          {!drillLoading && drill && drill.cells.length === 0 && (
            <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>
              No {drill.level === 'pincode' ? 'pincodes' : 'districts'} available for this unit
              {drill.level === 'pincode' ? ' (seed the pincode dataset).' : '.'}
            </div>
          )}
          {!drillLoading && drill && drill.cells.length > 0 && (
            <div data-testid="cov-drill-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8 }}>
              {drill.cells.map((cell) => {
                const color = cell.cell;
                const badge = overlapBadge(cell.overlap);
                const isPincode = drill.level === 'pincode';
                return (
                  <button key={cell.unit} type="button"
                    data-testid={`drill-${cell.unit}`}
                    onClick={() => (isPincode ? selectPincode(cell.unit) : drillDistrict(cell.unit))}
                    style={{ position: 'relative', textAlign: 'left', border: `1px solid ${color === 'red' ? '#e7b7b1' : 'var(--line)'}`, borderRadius: 9, background: COVERAGE_FILL[color], color: COVERAGE_INK[color], padding: '8px 10px', cursor: 'pointer' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700 }}>{cell.unit}</div>
                    <div style={{ fontSize: 10.5, opacity: 0.85 }}>
                      {cell.ownedSub}/{cell.totalSub} owned{cell.broadOnly ? ' · broad-only' : ''}
                    </div>
                    {badge.show && <OverlapDot kind={badge.kind!} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Legend (explicit-only wording — spec §2) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 14, fontSize: 11.5, color: 'var(--ink-2)' }}>
        <Legend sw={COVERAGE_FILL.green} label="Fully assigned — every sub-unit has an explicit owner" />
        <Legend sw={COVERAGE_FILL.yellow} label="Partially assigned — some sub-units owned, some not" />
        <Legend sw={COVERAGE_FILL.red} label="No explicit owner (broad/catch-all rule doesn't count)" />
        <Legend sw="var(--danger,#b5433a)" label="◗ badge = exec / manager overlap on the cell" ring />
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8, maxWidth: 720, lineHeight: 1.5 }}>
        Completeness is <b>explicit-only</b>: <b>green</b> = every sub-unit has a granular (district/pincode) owner · <b>yellow</b> = partially owned · <b>red</b> = no explicit owner. A broad state-level or catch-all rule does <b>not</b> count — a cell covered only by one still reads red (routing works, but there's no granular owner to fix).
      </div>

      {/* ── Territory panel ── */}
      {panel && (
        <TerritoryPanel
          key={`${panel.productGroupKey}|${panel.matchType}|${panel.matchValue}`}
          productGroupKey={panel.productGroupKey}
          productLabel={panel.productLabel}
          matchType={panel.matchType}
          matchValue={panel.matchValue}
          breadcrumbLabel={panel.breadcrumbLabel}
          staff={staff}
          staffGraph={staffGraph}
          fallbackUserId={fallbackUserId}
          onClose={() => setPanel(null)}
          onEditEscalation={onEditEscalation}
          onEditDealers={onEditDealers}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ── tiny presentational bits ──────────────────────────────────────────────────
type Crumb = { label: string; level: 'root' | 'state' | 'district' };

function OverlapDot({ kind }: { kind: 'exec' | 'manager' | 'both' }) {
  const bg = kind === 'manager' ? 'var(--violet,#6b4fd8)' : kind === 'both' ? 'var(--saffron,#d98a25)' : 'var(--warn,#a5741a)';
  return <span title={`${kind}-overlap`} style={{ position: 'absolute', top: 3, right: 4, width: 7, height: 7, borderRadius: 4, background: bg, boxShadow: '0 0 0 1px #fff' }} />;
}

function abbr(state: string): string {
  return state.length <= 6 ? state : state.slice(0, 5) + '…';
}
function cellTitle(g: string, s: string, c?: CoverageMatrixV2Response['cells'][number]): string {
  if (!c) return `${g} · ${s} · no data`;
  const tone = c.cell === 'green' ? 'fully assigned' : c.cell === 'yellow' ? 'partially assigned' : (c.broadOnly ? 'no explicit owner (broad rule covers)' : 'no assignment');
  const ov = c.overlap.exec || c.overlap.manager ? ` · overlap(${[c.overlap.exec ? 'exec' : '', c.overlap.manager ? 'manager' : ''].filter(Boolean).join('+')})` : '';
  return `${g} · ${s} · ${tone} (${c.ownedSub}/${c.totalSub})${ov}`;
}

function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-0)', margin: '0 0 4px' }}>{title}</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0, maxWidth: 720, lineHeight: 1.5 }}>{sub}</p>
    </div>
  );
}
function Stat({ v, k, color }: { v: React.ReactNode; k: string; color: CoverageColorV2 | 'neutral' }) {
  const fg = color === 'neutral' ? 'var(--ink-1)' : COVERAGE_INK[color];
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, background: 'var(--ivory-0)', padding: '10px 12px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: fg }}>{v}</div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{k}</div>
    </div>
  );
}
function Legend({ sw, label, ring }: { sw: string; label: string; ring?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 14, height: 14, borderRadius: ring ? 7 : 4, background: sw, border: '1px solid var(--line)', display: 'inline-block' }} /> {label}
    </span>
  );
}

const th: React.CSSProperties = { padding: '7px 8px', fontSize: 10.5, fontWeight: 700, color: 'var(--ink-2)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '7px 8px', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)' };
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-1)' };

export default CoverageMatrixV2;
